import { TooltipProvider } from "@radix-ui/react-tooltip";
import {
  Outlet,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/components/ui/use-toast";
import { hasActiveCellId } from "@/lib/cell";
import { isStandaloneBrowser } from "@/lib/runtime";
import useDeflowSessionStore from "@/stores/useDeflowSessionStore";
import useFigmaSessionStore from "@/stores/useFigmaSessionStore";
import usePluginShellState from "@/stores/usePluginShellState";
import {
  FigmaHostAuthTokens,
  HostAdapterContext,
} from "@/clients/host/adapter";
import { FigmaHostAdapter } from "@/clients/host/figma";
import {
  validateDeflowAccessToken,
  validateFigmaAccessToken,
} from "@/clients/auth";

export const Route = createFileRoute("/plugin/figma")({
  component: PluginLayout,
});

function PluginLayout() {
  const { setAuthenticated, setNotAuthenticated } = useDeflowSessionStore();
  const { setFigmaSession, setFigmaLastSignIn } = useFigmaSessionStore();
  const { setStatus } = usePluginShellState();
  const adapterRef = useRef<FigmaHostAdapter | null>(null);
  const [adapter, setAdapter] = useState<FigmaHostAdapter | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (adapterRef.current) {
      return;
    }

    if (isStandaloneBrowser()) {
      setStatus("failed");
      return;
    }

    const created = new FigmaHostAdapter(window.parent);
    adapterRef.current = created;
    setAdapter(created);

    const offlineHandler = () => {
      toast({
        title: "Uh oh! Bad connection?",
        description: "Functionality is limited while you're offline",
      });
    };
    window.addEventListener("offline", offlineHandler);

    void (async () => {
      const state = await created.initialize();
      setStatus("ready");

      const deflowLastSignIn = state.deflow?.lastSignIn;
      if (!deflowLastSignIn) {
        navigate({
          to: hasActiveCellId()
            ? "/plugin/figma/sign-up"
            : "/plugin/figma/region",
          replace: true,
        });
        return;
      }

      const validatedDeflowTokens = await validateDeflowAccessToken(
        state.deflow?.tokens,
      );

      const figmaLastSignIn = state.host?.lastSignIn;
      const figmaTokens = state.host?.tokens as
        | FigmaHostAuthTokens
        | undefined;
      const validatedFigmaTokens = figmaLastSignIn
        ? validateFigmaAccessToken(figmaTokens)
        : undefined;

      if (validatedFigmaTokens && figmaLastSignIn) {
        setFigmaSession({
          lastSignIn: figmaLastSignIn,
          tokens: validatedFigmaTokens.tokens,
        });
      } else if (figmaLastSignIn) {
        setFigmaLastSignIn({ lastSignIn: figmaLastSignIn });
        if (figmaTokens) {
          void created.clearHostAuth();
        }
      }

      if (validatedDeflowTokens) {
        if (
          validatedDeflowTokens.refresh &&
          validatedDeflowTokens.refreshedAt
        ) {
          void created.persistDeflowSession(
            validatedDeflowTokens.tokens,
            validatedDeflowTokens.refreshedAt,
          );
        }

        setAuthenticated({
          lastSignIn:
            validatedDeflowTokens.refreshedAt ?? deflowLastSignIn,
          tokens: validatedDeflowTokens.tokens,
        });
      } else {
        void created.clearDeflowSession();
        setNotAuthenticated();
        navigate({ to: "/plugin/figma/sign-in", replace: true });
      }
    })();

    return () => {
      window.removeEventListener("offline", offlineHandler);
      created.destroy();
      adapterRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HostAdapterContext.Provider value={adapter}>
      <TooltipProvider>
        <Outlet /> <Toaster />
      </TooltipProvider>
    </HostAdapterContext.Provider>
  );
}
